import React, {useState} from "react";
import {DeleteOutlined, DownloadOutlined} from "@ant-design/icons";
import {Button, message, Tooltip} from "antd";
import SharedService from "../../../services/shared/service";
import {convertBytes, downloadBase64File} from "../../../utils/file.utils";

const fileExtension = (fileName = "") => {
    const dot = fileName.lastIndexOf(".");
    if (dot < 0 || dot === fileName.length - 1) return "FILE";
    return fileName.slice(dot + 1).toUpperCase().slice(0, 4);
};

const badgeTone = (ext) => {
    if (ext === "PDF") return {bg: "#FDECEC", text: "#A4231D"};
    if (["DOC", "DOCX"].includes(ext)) return {bg: "#E6F1FC", text: "#1558B0"};
    if (["XLS", "XLSX", "CSV"].includes(ext)) return {bg: "#ECF9EE", text: "#1B7A31"};
    if (["JPG", "JPEG", "PNG", "GIF", "WEBP", "BMP"].includes(ext)) return {bg: "#FFF4E0", text: "#9B6B00"};
    return {bg: "#F1F3F7", text: "#55607A"};
};

const AttachmentCard = ({
                            attachment = null,
                            disabled = false,
                            user,
                            onDelete,
                        }) => {
    const [downloading, setDownloading] = useState(false);

    if (!attachment) return null;

    const ext = fileExtension(attachment.fileName);
    const tone = badgeTone(ext);
    const sizeText = typeof attachment.size === "number" ? `${convertBytes(attachment.size)} KB` : null;

    const handleDownload = async () => {
        if (disabled || downloading) return;

        try {
            setDownloading(true);

            const fileDto = await SharedService.get({
                token: user.token,
                url: `attachment/${attachment.id}/download`,
            });

            if (!fileDto?.base64File) {
                message.error("No file content returned from download endpoint.");
                return;
            }

            downloadBase64File({
                content: fileDto.base64File,
                fileName: fileDto.fileName ?? attachment.fileName ?? "attachment",
                type: fileDto.fileType ?? attachment.type ?? "application/octet-stream",
            });
        } catch (err) {
            console.error("Download failed:", err);
            message.error("Failed to download attachment.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                background: "#fff",
                border: "1px solid #E4E7EE",
                borderRadius: 10,
                opacity: disabled ? 0.6 : 1,
            }}
        >
            <div
                style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    background: tone.bg,
                    color: tone.text,
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: ".5px",
                    flexShrink: 0,
                }}
            >
                {ext}
            </div>
            <div style={{flex: 1, minWidth: 0}}>
                <Tooltip placement="top" title={attachment.fileName}>
                    <div
                        style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#0B1220",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {attachment.fileName}
                    </div>
                </Tooltip>
                <div style={{fontSize: 11.5, color: "#7A849A", marginTop: 2}}>
                    {attachment.type}
                    {sizeText ? ` · ${sizeText}` : ""}
                </div>
            </div>
            <Tooltip placement="top" title={downloading ? "Downloading..." : "Download"}>
                <Button
                    size="small"
                    type="text"
                    icon={<DownloadOutlined/>}
                    loading={downloading}
                    onClick={handleDownload}
                    disabled={disabled}
                />
            </Tooltip>
            <Tooltip placement="top" title="Delete">
                <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined/>}
                    disabled={disabled}
                    onClick={() => onDelete?.(attachment.id)}
                />
            </Tooltip>
        </div>
    );
};

export default AttachmentCard;
